import dayjs from 'dayjs';
import type {
    JournalEntrySource,
    JournalEntrySourceWire,
    JournalEntryStatus,
    JournalEntrySummary,
} from '@/api/models';
import type {
    JournalSourceFilter,
    JournalStatusFilter,
} from '@/api/handlers/resources/journal/requests';
import { PATH } from '@/router/PATH';
import { formatGel } from '@/pages/accounting/books/chartOfAccounts/utils';

export const JOURNAL_PAGE_SIZE = 15;

/** Stable column ids for the journal DataGrid. */
export const JOURNAL_COLUMN = {
    date: 'date',
    entryNumber: 'entryNumber',
    description: 'description',
    document: 'document',
    source: 'source',
    amount: 'amount',
    status: 'status',
} as const;

/** Segmented status filter order + i18n label keys (namespace `pages`). */
export const JOURNAL_STATUS_FILTERS: {
    id: JournalStatusFilter;
    labelKey: string;
}[] = [
    { id: 'ALL', labelKey: 'journal.filters.all' },
    { id: 'DRAFT', labelKey: 'journal.filters.draft' },
    { id: 'POSTED', labelKey: 'journal.filters.posted' },
    { id: 'REVERSED', labelKey: 'journal.filters.reversed' },
];

/**
 * Segmented origin filter order + i18n label keys. Sales and purchases are the
 * two an ordinary trading day produces (BOS-119), so they earn their own
 * chips; everything the ledger generates for itself is one more, "System",
 * which travels as the server's `sourceGroup` because it is a family rather
 * than a single origin (bos-api BOS-136).
 *
 * A shift's cash difference (bos-api BOS-164) is **System**, not a sixth chip.
 * Nobody keys it: the ledger writes it when a drawer is counted down, which is
 * the definition this filter already uses. The SOURCE column still names it
 * "Shift close", so a reader can tell one system entry from another.
 *
 * The chips read plural ("Sales") because they name a set; a row's own origin
 * reads singular ("Sale"). They are separate keys for that reason.
 */
export const JOURNAL_SOURCE_FILTERS: {
    id: JournalSourceFilter;
    labelKey: string;
}[] = [
    { id: 'ALL', labelKey: 'journal.sources.all' },
    { id: 'MANUAL', labelKey: 'journal.sources.manual' },
    { id: 'SALE', labelKey: 'journal.sources.salesFilter' },
    { id: 'PURCHASE', labelKey: 'journal.sources.purchasesFilter' },
    { id: 'SYSTEM', labelKey: 'journal.sources.system' },
];

/** i18n label key per origin, for the SOURCE column. */
export const JOURNAL_SOURCE_LABEL_KEY: Record<JournalEntrySource, string> = {
    MANUAL: 'journal.sources.manual',
    OPENING: 'journal.sources.opening',
    REVERSAL: 'journal.sources.reversal',
    CLOSING: 'journal.sources.closing',
    ROLLOVER: 'journal.sources.rollover',
    DEPRECIATION: 'journal.sources.depreciation',
    PAYROLL: 'journal.sources.payroll',
    VAT_SETTLEMENT: 'journal.sources.vatSettlement',
    DIVIDEND: 'journal.sources.dividend',
    ACQUISITION: 'journal.sources.acquisition',
    DISPOSAL: 'journal.sources.disposal',
    SALE: 'journal.sources.sale',
    PURCHASE: 'journal.sources.purchase',
    SUPPLIER_PAYMENT: 'journal.sources.supplierPayment',
    SHIFT: 'journal.sources.shift',
};

/**
 * What the SOURCE column shows for an entry.
 *
 * <p>An origin this build has never heard of renders as its own raw value —
 * `PAYOUT`, say — rather than as a blank cell or, worse, as whatever the
 * lookup happened to fall through to. A reader seeing "PAYOUT" learns
 * something true; a reader seeing "Manual" learns something false.</p>
 */
export const sourceLabelKey = (source: JournalEntrySourceWire): string | null =>
    (JOURNAL_SOURCE_LABEL_KEY as Record<string, string | undefined>)[source] ??
    null;

/**
 * Where an entry's `documentRef` points, when it points anywhere.
 *
 * A sale stamps the transaction id and a purchase the invoice number, so the
 * journal can send the reader to the document the figures came from rather
 * than leaving them to search for it. Every other origin's ref is free text
 * with nothing behind it, and gets no link.
 */
export const documentLink = (
    source: JournalEntrySourceWire | undefined,
    documentRef: string | null | undefined,
): string | null => {
    if (!documentRef) {
        return null;
    }
    if (source === 'SALE') {
        return `${PATH.SALES}?transaction=${encodeURIComponent(documentRef)}`;
    }
    if (source === 'PURCHASE') {
        return `${PATH.PURCHASES.ROOT}?invoice=${encodeURIComponent(documentRef)}`;
    }
    if (source === 'SUPPLIER_PAYMENT') {
        return `${PATH.SUPPLIERS}?payment=${encodeURIComponent(documentRef)}`;
    }
    return null;
};

/**
 * The origins that come from a document rather than from someone typing an
 * entry. Reversing one of these undoes the books while the document stays on
 * record (bos-api BOS-135), so the UI leads with the document instead.
 *
 * Only the three the app can actually navigate to are listed: a payroll or
 * depreciation run has no screen to send the reader to yet, so the drawer
 * offers them the books-only path with its warning rather than a dead link.
 */
export const OPERATIONAL_SOURCES = [
    'SALE',
    'PURCHASE',
    'SUPPLIER_PAYMENT',
] as const satisfies readonly JournalEntrySource[];

/**
 * The server's refusal code for reversing an entry a document produced
 * (bos-api BOS-135).
 *
 * The client's own list of operational origins only covers the ones this build
 * was written with; the 409 covers the rest. Branch on this, never on the
 * message — it is prose, and its last clause ("or pass force=true") is advice
 * for a caller holding an HTTP client, not for someone looking at a drawer.
 */
export const OPERATIONAL_ENTRY_CODE = 'OPERATIONAL_ENTRY';

/** The document behind an entry: where to send the reader, and what to call it. */
export type SourceDocument = {
    href: string | null;
    /** i18n key for the link's label ("Go to sale"). */
    labelKey: string;
    /** i18n key naming the source in the warning copy. */
    sourceLabelKey: string;
};

/**
 * The document an entry came from, or <c>null</c> when a person typed it.
 *
 * A `href` of `null` means "this came from a document, but there is no screen
 * for it" — a payroll run, say. The caller still withholds the plain Reverse
 * button; it just has nowhere to point.
 */
export const operationalDocument = (entry: {
    source?: JournalEntrySourceWire;
    documentRef?: string | null;
}): SourceDocument | null => {
    switch (entry.source) {
        case 'SALE':
            return {
                href: documentLink(entry.source, entry.documentRef),
                labelKey: 'journal.drawer.goToSale',
                sourceLabelKey: 'journal.sources.sale',
            };
        case 'PURCHASE':
            return {
                href: documentLink(entry.source, entry.documentRef),
                labelKey: 'journal.drawer.goToPurchase',
                sourceLabelKey: 'journal.sources.purchase',
            };
        case 'SUPPLIER_PAYMENT':
            return {
                href: documentLink(entry.source, entry.documentRef),
                labelKey: 'journal.drawer.goToSupplierPayment',
                sourceLabelKey: 'journal.sources.supplierPayment',
            };
        case 'PAYROLL':
            return {
                href: null,
                labelKey: 'journal.drawer.goToDocument',
                sourceLabelKey: 'journal.sources.payroll',
            };
        case 'DEPRECIATION':
            return {
                href: null,
                labelKey: 'journal.drawer.goToDocument',
                sourceLabelKey: 'journal.sources.depreciation',
            };
        case 'ACQUISITION':
            return {
                href: null,
                labelKey: 'journal.drawer.goToDocument',
                sourceLabelKey: 'journal.sources.acquisition',
            };
        case 'DISPOSAL':
            return {
                href: null,
                labelKey: 'journal.drawer.goToDocument',
                sourceLabelKey: 'journal.sources.disposal',
            };
        case 'DIVIDEND':
            return {
                href: null,
                labelKey: 'journal.drawer.goToDocument',
                sourceLabelKey: 'journal.sources.dividend',
            };
        default:
            return null;
    }
};

/** How much of a GUID is enough to recognise it in a column. */
const SHORT_ID_LENGTH = 8;

/**
 * What a document reference reads as in a column.
 *
 * A sale stamps its transaction id, which is a 36-character GUID: printed
 * whole it crowds out every other column and is no more recognisable than its
 * first eight characters. An invoice number or a payment reference is already
 * short and human-chosen, so it is left alone.
 */
export const documentRefLabel = (
    source: JournalEntrySourceWire | undefined,
    documentRef: string | null | undefined,
): string | null => {
    if (!documentRef) {
        return null;
    }
    return source === 'SALE' && documentRef.length > SHORT_ID_LENGTH
        ? `${documentRef.slice(0, SHORT_ID_LENGTH)}…`
        : documentRef;
};

/**
 * Soft status-pill styling. Following this theme's soft-chip philosophy the
 * pills render the same in light and dark. Posted is affirmative green; drafts
 * and reversed entries are neutral, reversed a touch more muted.
 */
export const JOURNAL_STATUS_CHIP: Record<
    JournalEntryStatus,
    { className: string; labelKey: string }
> = {
    POSTED: {
        className: 'bg-green-100 text-green-800',
        labelKey: 'journal.status.posted',
    },
    DRAFT: {
        className: 'bg-neutral-200 text-neutral-700',
        labelKey: 'journal.status.draft',
    },
    REVERSED: {
        className: 'bg-neutral-200 text-neutral-500',
        labelKey: 'journal.status.reversed',
    },
};

/** `DD/MM/YYYY` display for an ISO date. */
export const formatEntryDate = (iso: string): string =>
    iso ? dayjs(iso, 'YYYY-MM-DD').format('DD/MM/YYYY') : '';

/**
 * What to show in the ENTRY # column. A real number when assigned; otherwise
 * `JE-…` for a posted entry whose number is pending, or `–` for a draft.
 */
export const entryNumberDisplay = (
    summary: Pick<JournalEntrySummary, 'entryNumber' | 'status'>,
    pendingLabel: string,
): string => {
    if (summary.entryNumber) {
        return summary.entryNumber;
    }
    return summary.status === 'POSTED' ? pendingLabel : '–';
};

/** Entry total for the AMOUNT column. */
export const formatEntryAmount = (amountTetri: number): string =>
    formatGel(amountTetri);

/**
 * Page numbers to render, with `'ellipsis'` gaps — a 7-wide window that always
 * shows first/last and a run around the current page.
 */
export const getPaginationWindow = (
    totalPages: number,
    page: number,
): (number | 'ellipsis')[] => {
    if (totalPages <= 7) {
        return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    const window = new Set<number>([1, totalPages, page]);
    for (let offset = 1; offset <= 1; offset += 1) {
        window.add(Math.max(1, page - offset));
        window.add(Math.min(totalPages, page + offset));
    }
    const sorted = Array.from(window).sort((a, b) => a - b);
    const result: (number | 'ellipsis')[] = [];
    let previous = 0;
    for (const current of sorted) {
        if (current - previous > 1) {
            result.push('ellipsis');
        }
        result.push(current);
        previous = current;
    }
    return result;
};
